import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import Icon from '../../../../Icon2/Icon';
import SidebarModal from '../../../../SideBarModal/SideBarModal';
import AssetCardVertical from '../../../../AssetCardVertical/AssetCardVertical';
import Button from '../../../../Button/Button';
import Tooltip from '../../../../Tooltip/Tooltip';
import { Truncate } from '../../../../Truncate/Truncate';
import lodashIsEmpty from 'lodash/isEmpty';

import { AssetData, AssetLoaderProps, AssetThumbnailRefreshProps } from '../../../utils/types';
import { buildSecureAssetDeliveryUrl, isPrivateAssetAccess } from '../../../../../utils/secureAssetDeliveryUrl';
import AssetPickerAccessBadge from '../ListView/AssetPickerAccessBadge';

export const ASSET = {
  REQUESTING: 'ASSET_REQUESTING',
  SUCCESS: 'ASSET_SUCCESS',
  GET_ALL: 'GET_ALL_ASSET',
  SET_ALL: 'SET_ALL_ASSET',
  GET_SINGLE: 'GET_SINGLE_ASSET',
  SET_SINGLE: 'SET_SINGLE_ASSET',
  ERROR: 'ASSET_ERROR',
  SET_VIEW: 'SET_VIEW_ASSET',
  GET_FOLDER: 'GET_FOLDER_ASSET',
  SET_FOLDER: 'SET_FOLDER_ASSET',
  SET_SELECTED_ASSETS: 'SET_SELECTED_ASSETS',
  SET_SELECTED_ASSETS_DATA: 'SET_SELECTED_ASSETS_DATA',
  SET_ASSET_PUBLISH: 'SET_ASSET_PUBLISH',
  SET_SELECTED_FOLDER_LABEL: 'SET_SELECTED_FOLDER_LABEL',
  MAX_BATCH_UPLOAD_SIZE: 100,
  TABLE_ASSET_WIDTH: 40,
  TABLE_ASSET_HEIGHT: 40,
  ASSET_WIDTH_HORIZONTAL: 60,
  ASSET_HEIGHT_HORIZONTAL: 60,
  ASSET_WIDTH_VERTICAL: 180,
  ASSET_HEIGHT_VERTICAL: 256,
  ASSET_THUMBNAIL_WIDTH: 180,
  ASSET_THUMBNAIL_HEIGHT: 180,
  SCALE_FACTOR: 0.25
}

export const AssetThumbnailRefresh: React.FC<AssetThumbnailRefreshProps> = ({ count, reFetchAssetData }) => {
  return (
    <div data-test-id="cs-assets-refresh">
      <span className="asset-thumbnail-total-count">{count || 0} items</span>
      <Button
        size="regular"
        buttonType="tertiary"
        onlyIcon={true}
        icon={'v2-Refresh'}
        onlyIconHoverColor={'primary'}
        version="v2"
        onClick={reFetchAssetData}
        testId={'cs-assets-refresh-button'}
      />
    </div>
  );
};

export const closeDropdown = (
  setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>,
  setActionSelected: React.Dispatch<React.SetStateAction<string>>
) => {
  setShowDropdown(false);
  setActionSelected('');
};

export const FullScreen = () => {
  return (
    <SidebarModal modalContent={<div>Expanded view</div>} shouldCloseOnOverlayClick={true}>
      <Icon version="v2" icon="Expand" size="medium" testId="cs-assets-expand" />
    </SidebarModal>
  );
};

export const getAssetIconName = (type: string) => {
  let svgName = 'DOC2';
  if (type) {
    switch (true) {
      case type === 'folder':
        svgName = 'Folder';
        break;
      case type === 'pdf' || type === 'PDF':
        svgName = 'PDF2';
        break;
      case type === 'excel' || type === 'spreadsheet':
        svgName = 'XLS';
        break;
      case type === 'presentation' || type === 'ppt':
        svgName = 'PPT';
        break;
      case type === 'document':
        svgName = 'DOC2';
        break;
      case type === 'txt':
        svgName = 'TXT';
        break;
      case type === 'zip':
        svgName = 'Zip';
        break;
      case type === 'mp4' || type === 'video':
        svgName = 'MP4';
        break;
      case type === 'avi':
        svgName = 'AVI';
        break;
      case type === 'audio':
        svgName = 'MP3';
        break;
      case type === 'html':
        svgName = 'HTML2';
        break;
      case type === 'archive':
        svgName = 'ZIP';
        break;
      case type === 'json':
        svgName = 'JSON';
        break;
      case type === 'xml':
        svgName = 'XML';
        break;
      case type === 'javascript':
        svgName = 'JS';
        break;
      case type === 'exe':
        svgName = 'EXE';
        break;
      case type === 'csv':
        svgName = 'CSV';
        break;
      case type === 'psd':
        svgName = 'PSD';
        break;
      case type === 'rtf':
        svgName = 'RTF';
        break;
      case type === 'ai':
        svgName = 'AI';
        break;
      case type === 'css':
        svgName = 'CSS';
        break;
      case type === 'jpeg':
        svgName = 'JPG';
        break;
      case type === 'jpg':
        svgName = 'JPG';
        break;
    }
    if (type === 'image/tiff') {
      svgName = 'Document';
    }
    return svgName;
  }
};

export const getAssetType = (assetObj: AssetData) => {
  try {
    let assetType = 'brokenImage';
    if (assetObj && (assetObj.is_dir || assetObj.isDir)) {
      return 'folder';
    }
    if (assetObj && assetObj.content_type && Array.isArray(assetObj.content_type)) {
      assetObj.content_type = assetObj.content_type[0];
    }
    if (assetObj && assetObj.content_type) {
      switch (true) {
        case assetObj.content_type.indexOf('photoshop') > -1:
          assetType = 'psd';
          break;
        case assetObj.content_type.indexOf('image') > -1:
          assetType = 'image';
          break;
        case assetObj.content_type.indexOf('folder') > -1:
          assetType = 'folder';
          break;
        case assetObj.content_type.indexOf('pdf') > -1:
          assetType = 'pdf';
          break;
        case assetObj.content_type.indexOf('excel') > -1:
          assetType = 'spreadsheet';
          break;
        case assetObj.content_type.indexOf('presentation') > -1:
          assetType = 'ppt';
          break;
        case assetObj.content_type.indexOf('json') > -1:
          assetType = 'json';
          break;
        case assetObj.content_type.indexOf('text/plain') > -1:
          assetType = 'txt';
          break;
        case assetObj.content_type.indexOf('text/html') > -1:
          assetType = 'html';
          break;
        case assetObj.content_type.indexOf('zip') > -1:
          assetType = 'archive';
          break;
        case assetObj.content_type.indexOf('video/mp4') > -1:
          assetType = 'mp4';
          break;
        case assetObj.content_type.indexOf('video/avi') > -1:
          assetType = 'avi';
          break;
        case assetObj.content_type.indexOf('video') > -1:
          assetType = 'video';
          break;
        case assetObj.content_type.indexOf('audio') > -1:
          assetType = 'audio';
          break;
        case assetObj.content_type.indexOf('spreadsheet') > -1:
          assetType = 'spreadsheet';
          break;
        case assetObj.content_type.indexOf('javascript') > -1:
          assetType = 'javascript';
          break;
        case assetObj.content_type.indexOf('document') > -1:
          assetType = 'document';
          break;
        case assetObj.content_type.indexOf('powerpoint') > -1:
          assetType = 'ppt';
          break;
        case assetObj.content_type.indexOf('text/xml') > -1:
          assetType = 'xml';
          break;
        case assetObj.content_type.indexOf('x-msdownload') > -1:
          assetType = 'exe';
          break;
        case assetObj.content_type.indexOf('text/csv') > -1:
          assetType = 'csv';
          break;
        case assetObj.content_type.indexOf('text/rtf') > -1:
          assetType = 'rtf';
          break;
        case assetObj.content_type.indexOf('postscript') > -1:
          assetType = 'ai';
          break;
        case assetObj.content_type.indexOf('text/css') > -1:
          assetType = 'css';
          break;
      }
      if (assetObj.content_type.indexOf('image/tiff') > -1) {
        assetType = 'document';
      }
      if ('is_dir' in assetObj && assetObj.is_dir) {
        assetType = 'folder';
      }
      return assetType;
    }
    return assetType;
  } catch (error) {
    console.error('commonUtils getAssetType error:', error);
    return ' ';
  }
};

export const getAssetUrl = (assetObj: AssetData, stackObj: { isAssetDownloadPublic: boolean }, noResize?: boolean) => {
  assetObj.content_type = assetObj.content_type ? (assetObj.content_type as string) : (assetObj.type as string);
  if (assetObj.content_type.indexOf('image') > -1) {
    if (stackObj && stackObj.isAssetDownloadPublic === false) {
      const l: HTMLAnchorElement = document.createElement('a');
      l.href = assetObj.url || '';
      const queryString = l?.search;
      const branchValue = new URLSearchParams(queryString)?.get('branch');
      if (branchValue) {
        return branchValue !== 'main' ? `api${l.pathname}${queryString}` : `api${l.pathname}`;
      }
      return `api${l.pathname}`;
    }
    if (!Array.isArray(assetObj.url) && assetObj.url?.indexOf('?') === -1 && !noResize) {
      const imageurl = `${assetObj.url}`;
      assetObj.url = imageurl;
    }
    if (Array.isArray(assetObj.url) && assetObj.url[0]?.indexOf('?branch') > -1) {
      const imageurl = `${assetObj.url[0]}`;
      assetObj.url = imageurl;
    }
    if (Array.isArray(assetObj.url) && assetObj.url[0]?.indexOf('?branch') === -1) {
      const imageurl = `${assetObj.url[0]}`;
      assetObj.url = imageurl;
    }
    return assetObj.url;
  } else {
    return undefined;
  }
};

export const getAssetIcon = (
  assetObj: AssetData,
  stackObj: { isAssetDownloadPublic: boolean },
  location?: { branch: string }
) => {
  let imageURL;
  const noResize = true;
  if (assetObj) {
    const updatedAssetObj = {
      ...assetObj,
      content_type: assetObj.content_type ?? assetObj.type
    };

    if (updatedAssetObj.content_type?.indexOf('image') > -1 && !updatedAssetObj.content_type.includes('photoshop')) {
      imageURL =
        !lodashIsEmpty(location) && !location.branch
          ? `api${updatedAssetObj.url}`
          : getAssetUrl(updatedAssetObj, stackObj, noResize);
    } else {
      const assetType = getAssetType(updatedAssetObj);
      imageURL = getAssetIconName(assetType);
    }

    return imageURL;
  }
};


const AssetTooltipContent = ({ asset, localesWithFallback, spacesWithTitles = [] }: {
  asset: AssetData,
  localesWithFallback: any[],
  spacesWithTitles?: Array<{ uid: string; title: string }>
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const getSpaceName = (spaceUid?: string): string => {
    if (!spaceUid || !spacesWithTitles.length) return '';
    const space = spacesWithTitles.find(s => s.uid === spaceUid);
    return space?.title || '';
  };

  const formatAssetPath = (pathArray: Array<{ title: string; uid: string }> | undefined, spaceUid?: string) => {
    const spaceName = getSpaceName(spaceUid);
    const spacePrefix = spaceName ? `/${spaceName}` : '';
    if (!pathArray || pathArray.length === 0) {
      return spacePrefix ? `${spacePrefix}/` : '/..';
    }
    return `${spacePrefix}/` + pathArray.map(p => p.title).join('/') + '/';
  };

  return (
    <div className="thumbnail-tooltip-content">
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>Title:</strong>
            <Truncate maxChar={30}>{asset.title || '-'}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>File Name:</strong>
            <Truncate maxChar={24}>{(asset as any).file_name || asset.name || '-'}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>File Type:</strong>
            <Truncate maxChar={24}>{(asset as any).file_name?.split('.').pop() || '-'}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>File Path:</strong>
            <Truncate maxChar={24}>{formatAssetPath(asset.path, asset.space_uid)}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>Content Type:</strong>
            <Truncate maxChar={24}>{asset.content_type || '-'}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <strong>Size:</strong> {asset.file_size ? formatFileSize(asset.file_size) : '-'}
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <div className="thumbnail-tooltip-container">
            <strong>Dimensions:</strong>
            <Truncate maxChar={30}>{(asset.dimensions && asset.dimensions?.width && asset.dimensions?.height) ? `${asset.dimensions.width} x ${asset.dimensions.height}` : '-'}</Truncate>
          </div>
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <strong>Created At:</strong> {asset.created_at ? formatDate(asset.created_at) : '-'}
        </span>
      </div>
      <div className="thumbnail-tooltip-row">
        <span className="thumbnail-tooltip-text">
          <strong>Last Modified At:</strong> {asset.updated_at ? formatDate(asset.updated_at) : '-'}
        </span>
      </div>
      {!asset.is_dir && (
        <div className="thumbnail-tooltip-row">
          <span className="thumbnail-tooltip-text">
            <strong>Language:</strong> {localesWithFallback?.find(locale => locale?.code === asset?.locale)?.name || '-'}
          </span>
        </div>
      )}
    </div>
  );
};

export const AssetThumbnail: React.FC<any> = (props) => {
  const {
    data,
    isMultiple,
    setSpaceAssetsPicked,
    breadcrumbClickHandler,
    isSelected,
    showSelectedAssets,
    localesWithFallback,
    spacesWithTitles = [],
    isMoveTarget = false,
    assetPrivacyEnabled = false,
  } = props;
  const { is_dir, name } = data;
  const isDir = !!(is_dir || (data as any).isDir);
  let title = data.title || data.name;

  let testId;
  if (isDir) {
    testId = `am-v2-space-assets-thumbnail-folder-${data.uid}`;
  } else {
    testId = `am-v2-space-assets-thumbnail-${data.uid}`;
  }
  let assetURL: string;

  const computeCustomDimension = (dimension) => {
    const { height: oh, width: ow } = dimension || {};

    const isValidNumber = (n) => typeof n === 'number' && !isNaN(n);
    const scale = isValidNumber(ASSET?.SCALE_FACTOR) ? ASSET.SCALE_FACTOR : 1;

    if (!isValidNumber(ow) || !isValidNumber(oh)) {
      return { width: 180, height: 256 }; // fallback default
    }

    return {
      width: Math.round(ow * scale),
      height: Math.round(oh * scale),
    };
  };

  const customDimension = computeCustomDimension(data?.dimensions);

  if (!isDir) {
    const url = getAssetIcon(data, { isAssetDownloadPublic: true });
    const dimension = `height=${customDimension?.height}&width=${customDimension?.width}`;
    assetURL = `${url?.includes('?') ? `${url}&${dimension}` : `${url}?${dimension}`}`;
    assetURL = buildSecureAssetDeliveryUrl(assetURL, {
      isPrivate: isPrivateAssetAccess(data as never),
    }) as string;
  } else {
    title = name;
    assetURL = '/static/images/Folder.svg';
  }

  const onCardFolderClick = (
    isDir: boolean,
    data: AssetData,
  ) => {
    if (isDir) {
      // console.log('Directory clicked:', data.title);
      const crumbs = { name: data.title, root: false, folder_uid: data.uid, ...data };
      breadcrumbClickHandler(crumbs);
    } else {
      // console.log('File clicked:', data.title);
    }
  };


  const isInvalidAsset = data?.invalid_asset === true;

  const scanStatusValue: string | undefined = data?._asset_scan_status;

  const accessType = (data as never as { settings?: { privacy_settings?: { asset_access?: string } } })
    ?.settings?.privacy_settings?.asset_access;
  const accessSpaceUid = (data as never as { space_uid?: string })?.space_uid;
  const accessSpaceName =
    spacesWithTitles.find((s: { uid: string; title: string }) => s.uid === accessSpaceUid)?.title || '';
  const accessBadge =
    assetPrivacyEnabled && !isDir && (accessType === 'public' || accessType === 'private') ? (
      <AssetPickerAccessBadge
        mode={accessType === 'private' ? 'private' : 'public'}
        spaceName={accessSpaceName}
        variant="badge"
      />
    ) : undefined;

  const assetCardClickHandler = (e: any): void => {
    if (isInvalidAsset) {
      return;
    }
    
    setSpaceAssetsPicked((prevSpaceAssetsPicked) => {
      const isAlreadyPicked = prevSpaceAssetsPicked.some((pAsset: AssetData) => pAsset.uid === data.uid);

      if (!isMultiple) {
        // In single selection mode, we have to persist the same whenever clicked on same asset
        return [data];
      } else {
        // Multiple selection mode - add to or remove from the array
        if (!isAlreadyPicked) {
          return [...prevSpaceAssetsPicked, data];
        } else {
          return prevSpaceAssetsPicked.filter((pAsset: AssetData) => pAsset.uid !== data.uid);
        }
      }
    });
  };

  // In V2 AssetCardVertical, the Venus having the click only over the check box or radio button area, 
  // so we need to capture the click event on the card
  const assetCardClickCapture = (e: any): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isMoveTarget) {
      if (isDir) {
        onCardFolderClick(isDir, data);
      }
      return;
    }
    if (isDir) {
      onCardFolderClick(isDir, data);
    } else if (!isInvalidAsset) {
      assetCardClickHandler(e);
    }
  };

  const assetType = getAssetType(data);

  if (isDir) {
    return (
      <div onClickCapture={assetCardClickCapture}>
        <AssetCardVertical
          cardData={data}
          onCardClick={() => onCardFolderClick(isDir, data)}
          title={data.title || data.name}
          assetType={assetType}
          version="v2"
          testId={testId}
        />
      </div>
    );
  }

  const assetCard = (
    <div
      onClickCapture={assetCardClickCapture}
      className={cn('ThumbnailView__card-thumb-wrapper', {
        'ThumbnailView__card-thumb-wrapper--selected': isSelected
      })}
    >
      <AssetCardVertical
        isRadio={!isMultiple}
        checked={isSelected}
        onCardClick={assetCardClickHandler}
        cardData={data}
        title={title}
        assetType={assetType}
        size={data?.file_size}
        locale={localesWithFallback?.find((locale) => locale.code === data?.locale)?.name || data?.locale}
        imageHeight={data?.dimensions && data?.dimensions?.height}
        imageWidth={data?.dimensions && data?.dimensions?.width}
        height={data?.dimensions && data?.dimensions?.height}
        width={data?.dimensions && data?.dimensions?.width}
        // For a Lottie the player needs the plain delivery URL — `assetURL` above is a
        // resized image URL / file-type icon path (getAssetIcon), never the raw file.
        assetUrl={data?.isLottie ? (buildSecureAssetDeliveryUrl(data?.url ?? '', { isPrivate: isPrivateAssetAccess(data as never) }) as string) : assetURL}
        isLottie={data?.isLottie}
        version="v2"
        testId={testId}
        fitImage={true}
        isCardDisabled={isInvalidAsset}
        subtitleBadge={accessBadge}
        hoverText={data?.errorMessage?.trim()}
        previewIcon={{
          showPreviewIcon:
            (assetType !== 'image' &&
              !!(
                data?.assetType &&
                data?.assetType !== 'undefined' &&
                typeof data?.assetType === 'object' &&
                data?.assetType?.previewImageUrl
              )),
          url: data?.assetType &&
                data?.assetType !== 'undefined' &&
                typeof data?.assetType === 'object'
              ? data?.assetType?.previewImageUrl
              : undefined,
        }}
        assetScanStatus={scanStatusValue}
      />
    </div>
  );

  if (isInvalidAsset) {
    return assetCard;
  }

  return (
    <Tooltip
      content={<AssetTooltipContent asset={data} localesWithFallback={localesWithFallback} spacesWithTitles={spacesWithTitles} />}
      position="top"
      showArrow={true}
      maxWidth={300}
      delay={[500, 0]}
      zIndex={9999}
      interactive={false}
      hideOnClick={false}
      appendTo={() => document.body}
      variantType="basic"
      customTooltipCss="tippy-box-basic thumbnail-tooltip-custom"
    >
      {assetCard}
    </Tooltip>
  );
};

export const GridSkeleton = ({ num }: { num: number }) => {
  return (
    <>
      {Array(num)
        .fill(1)
        .map((d, i) => {
          return (
            <div key={i} className="ThumbnailView__card AssetFolder ThumbnailView__card--skeleton">
              <div className="ThumbnailView__asset"></div>
              <div className="ThumbnailView__asset-name">
                <div className="ThumbnailView__skeleton-med"></div>
                <div className="ThumbnailView__skeleton-small"></div>
              </div>
            </div>
          );
        })}
    </>
  );
};

export const loadMoreData = (
  entries: IntersectionObserverEntry[],
  shouldLoad: any,
  requesting: any,
  fetch: () => void
) => {
  const target = entries[0];
  if (target.isIntersecting && shouldLoad) {
    if (!requesting) fetch();
  }
};

export const AssetLoader: React.FC<AssetLoaderProps> = ({ fetch, shouldLoad, requesting }) => {
  const loader = useRef<HTMLDivElement | null>(null);
  const loadMore = useCallback(
    (entries: IntersectionObserverEntry[]) => loadMoreData(entries, shouldLoad, requesting, fetch),
    [fetch, requesting, shouldLoad]
  );

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    const observer = new IntersectionObserver(loadMore, options);
    const loaderCurrent = loader.current;
    if (loaderCurrent) {
      observer.observe(loaderCurrent);
    }

    return () => {
      if (loaderCurrent) {
        observer.unobserve(loaderCurrent);
      }
    };
  }, [loader, loadMore]);

  return (
    <>
      <div ref={loader} className="ThumbnailView__loader" data-test-id="cs-asset-loader" />
      {shouldLoad && <GridSkeleton num={1} />}
    </>
  );
};